import { useState } from "react";
import { Check, Minus, ArrowRight, Info } from "lucide-react";
import { Reveal, Eyebrow } from "../components/anim";
import Starfield from "../components/Starfield";
import PackageSelector from "../components/PackageSelector";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const PACKAGE_COLS = [
  { id: "basic", name: "Basic", price: "$300" },
  { id: "standard", name: "Standard", price: "$450" },
  { id: "premium", name: "Premium", price: "$650" },
];

const PACKAGE_ROWS = [
  { label: "Pages", values: ["2–3", "Up to 5", "Up to 7"] },
  { label: "Business information and photos", values: [true, true, true] },
  { label: "Search engine setup", values: ["Basic", "Enhanced", "Enhanced"] },
  { label: "Contact or quote request form", values: [false, true, true] },
  { label: "Photo gallery and customer reviews", values: [false, true, true] },
  { label: "Access to website visitor stats", values: [false, true, true] },
  { label: "AI assistant to answer questions and collect inquiries", values: [false, false, true] },
  { label: "Automatic inquiry confirmation emails", values: [false, false, true] },
];

const CARE_COLS = [
  { id: "essential", name: "Essential Care", price: "$50/month" },
  { id: "premium", name: "Premium Care", price: "$100/month" },
];

const CARE_ROWS = [
  { label: "Website hosting included", values: [true, true] },
  { label: "Small updates", values: ["4 per month", "8 per month"] },
  { label: "Larger changes", values: ["Priced separately", "1 per month"] },
  { label: "Response time", values: ["Within 2 business days", "Within 1 business day"] },
];

const INCLUDED_ITEMS = [
  {
    q: "What counts as a small update?",
    a: "Changing business hours, updating one price, replacing one photo, editing a short paragraph, or updating a link. Each individual update counts as one change.",
  },
  {
    q: "What counts as a larger change?",
    a: "Adding one standard page that matches your website's design, adding a new section, or rearranging an existing page. Full redesigns, online stores, booking systems, and custom features are quoted separately.",
  },
  {
    q: "How does monthly pricing work?",
    a: "Every website requires $15/month hosting. Essential Care replaces that with a total of $50/month. Premium Care replaces it with a total of $100/month. Hosting is included in both care plans.",
  },
  {
    q: "What if I only want hosting?",
    a: "Hosting is $15/month. Website changes are quoted separately. We respond to requests within 5 business days.",
  },
  {
    q: "What's included in search engine setup?",
    a: "Every package includes page titles and descriptions for search engines. Standard and Premium also include local keyword targeting, Google Search Console setup, and sitemap submission. Premium adds structured business information to help search engines understand your business. This covers initial setup, not ongoing SEO management. Rankings aren't guaranteed.",
  },
  {
    q: "What can I see in my website stats?",
    a: "Standard and Premium include access to visitor counts, traffic sources, popular pages, and tracked clicks on call and contact buttons.",
  },
  {
    q: "What does the AI assistant do?",
    a: "Premium includes an assistant that answers questions using your business information and collects contact details and service requests from interested visitors. Premium also includes automatic emails confirming receipt of customer inquiries.",
  },
  {
    q: "Are there other costs?",
    a: "Domain registration and any paid AI, email, booking, or other third-party services are separate and agreed on before setup.",
  },
  {
    q: "How do monthly updates work?",
    a: "Send us your requested changes and any text or photos needed. We'll respond within your plan's timeframe and confirm when the work will be completed. Response time means acknowledgment, not guaranteed completion. Unused changes don't roll over. Additional work is quoted for approval before we begin.",
  },
];

const Cell = ({ v }) => {
  if (v === true) return <Check className="mx-auto h-4 w-4 text-emerald-400" />;
  if (v === false) return <Minus className="mx-auto h-4 w-4 text-slate-600" />;
  return <span className="text-xs font-medium text-slate-200">{v}</span>;
};

const ComparisonTable = ({ cols, rows, testId }) => (
  <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/5 backdrop-blur" data-testid={testId}>
    <table className="w-full min-w-[560px] text-left">
      <thead>
        <tr className="border-b border-white/10">
          <th className="px-5 py-5 text-sm font-semibold text-white">Feature</th>
          {cols.map((c) => (
            <th key={c.id} className="px-4 py-5 text-center">
              <span className="block font-display text-base font-bold text-white">{c.name}</span>
              <span className="block text-xs font-medium text-blue-400">{c.price}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label} className="border-b border-white/5 last:border-0">
            <td className="px-5 py-4 text-sm text-slate-300">{row.label}</td>
            {row.values.map((v, i) => (
              <td key={i} className="px-4 py-4 text-center">
                <Cell v={v} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Pricing() {
  const [selectorOpen, setSelectorOpen] = useState(false);

  return (
    <main className="relative min-h-screen overflow-hidden pt-28 pb-20" data-testid="pricing-page">
      <Starfield />
      <div className="pointer-events-none absolute -top-32 right-[-10%] h-[420px] w-[420px] rounded-full bg-violet-600/15 blur-[130px]" />
      <div className="pointer-events-none absolute top-[45%] left-[-10%] h-[420px] w-[420px] rounded-full bg-blue-600/15 blur-[130px]" />
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Pricing</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold tracking-tight text-white sm:text-6xl">
            Choose Your Website
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-400">
            A one-time payment to build your website.
          </p>
        </Reveal>

        <Reveal delay={0.08} className="mt-12">
          <ComparisonTable cols={PACKAGE_COLS} rows={PACKAGE_ROWS} testId="package-table" />
        </Reveal>

        <Reveal delay={0.12}>
          <div
            className="mt-6 flex gap-3 rounded-2xl border border-blue-400/30 bg-blue-500/10 p-5"
            data-testid="hosting-note"
          >
            <Info className="h-5 w-5 shrink-0 text-blue-400" />
            <p className="text-sm leading-relaxed text-slate-200">
              Every website includes required hosting at <strong className="text-white">$15/month</strong>, billed
              separately from the one-time website price. Want ongoing updates? Choose a care plan below.
            </p>
          </div>
        </Reveal>

        <Reveal className="mt-20">
          <Eyebrow>Optional Monthly Care Plans</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-5xl">
            Want Us to Handle Updates?
          </h2>
          <p className="mt-4 max-w-2xl text-lg text-slate-400">Upgrade your monthly hosting to a care plan.</p>
        </Reveal>

        <Reveal delay={0.08} className="mt-10">
          <ComparisonTable cols={CARE_COLS} rows={CARE_ROWS} testId="care-table" />
        </Reveal>

        <Reveal delay={0.12}>
          <div
            className="mt-6 flex gap-3 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-5"
            data-testid="care-note"
          >
            <Info className="h-5 w-5 shrink-0 text-emerald-400" />
            <p className="text-sm leading-relaxed text-slate-200">
              Care plans include your $15 hosting. Your total is <strong className="text-white">$50 or $100 per
              month</strong> — not an additional charge on top of hosting. Without a care plan, you pay $15/month
              for hosting.
            </p>
          </div>
        </Reveal>

        <Reveal className="mt-14 text-center">
          <button
            onClick={() => setSelectorOpen(true)}
            data-testid="get-started-btn"
            className="group inline-flex items-center gap-2.5 rounded-full bg-blue-600 px-10 py-4.5 font-display text-lg font-bold text-white shadow-xl shadow-blue-600/30 transition-all hover:scale-[1.02] hover:bg-blue-500"
          >
            Get Started
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
        </Reveal>

        <Reveal className="mt-24">
          <div className="text-center">
            <div className="flex justify-center"><Eyebrow>Details</Eyebrow></div>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
              What's Included?
            </h2>
          </div>
          <div
            className="mx-auto mt-10 max-w-3xl rounded-3xl border border-white/10 bg-white/5 px-6 py-3 backdrop-blur sm:px-9"
            data-testid="included-accordion"
          >
            <Accordion type="single" collapsible className="w-full">
              {INCLUDED_ITEMS.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                  <AccordionTrigger
                    data-testid={`included-trigger-${i}`}
                    className="text-left text-sm font-semibold text-white hover:text-blue-400"
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-400">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Reveal>
      </div>
      <PackageSelector open={selectorOpen} onClose={() => setSelectorOpen(false)} />
    </main>
  );
}
